import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { followUpEvidenceQuestion, qaHref } from '../contextLinks';

// SQA-66: Owner queue UI surface — shared queue view.
//
// Parameterized read surface over GET /api/queues/<slug>. The API still owns
// queue membership, counts, freshness, and sort. This view turns the same queue
// payload into role-specific decision lanes so Vince/Nate do not have to parse a
// generic work list before they can tell what kind of judgment is being asked of
// them. Work-item-naming compliant ([INF-198]): team-facing copy uses
// "Captured Follow-ups", never "action item".

interface QueueCounts {
  overdue: number;
  due_today: number;
  blocked: number;
  waiting: number;
}

interface QueueFreshness {
  newest_item_at: string | null;
  oldest_active_at: string | null;
}

interface QueueItem {
  id: string;
  type: string | null;
  status: string;
  priority: string;
  owner_email: string | null;
  accountable_email: string | null;
  queue_key: string | null;
  resolved_queue_key: string | null;
  description: string;
  due_at: string | null;
  due_date: string | null;
  waiting_on: string | null;
  blocked_reason: string | null;
  contact_id: string | null;
  source: string | null;
  source_ref: string | null;
  confidence: number | null;
  reviewed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  is_overdue: boolean;
  is_due_today: boolean;
}

interface QueueResponse {
  queue_key: string;
  generated_at: string;
  freshness: QueueFreshness;
  counts: QueueCounts;
  items: QueueItem[];
}

interface OwnerQueueViewProps {
  slug: string;
  displayName: string;
  roleKey: string;
}

interface QueueLane {
  id: string;
  title: string;
  empty: string;
  matches: (item: QueueItem) => boolean;
}

interface RoleProfile {
  eyebrow: string;
  title: string;
  mission: string;
  primaryMetric: string;
  pressureLabel: string;
  lanes: QueueLane[];
}

const PRIORITY_CHIP: Record<string, string> = {
  urgent: 'bg-red-50 ring-red-300 text-red-900',
  high: 'bg-orange-50 ring-orange-300 text-orange-900',
  normal: 'bg-blue-50 ring-blue-200 text-blue-900',
  low: 'bg-slate-50 ring-slate-200 text-slate-700',
};

const STATUS_CHIP: Record<string, string> = {
  open: 'bg-blue-50 ring-blue-200 text-blue-900',
  in_progress: 'bg-amber-50 ring-amber-200 text-amber-900',
  waiting: 'bg-purple-50 ring-purple-200 text-purple-900',
  blocked: 'bg-rose-50 ring-rose-200 text-rose-900',
  done: 'bg-green-50 ring-green-200 text-green-900',
  dismissed: 'bg-slate-100 ring-slate-300 text-slate-700',
};

function chip(classes: string, label: string): JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${classes}`}
    >
      {label}
    </span>
  );
}

function formatTimestamp(iso: string | null): string {
  if (iso === null) return 'Unavailable';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDate(iso: string | null): string {
  if (iso === null) return 'No due date';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function normalizeType(type: string | null): string {
  return type ? type.replaceAll('_', ' ') : 'unclassified';
}

function needsToday(item: QueueItem): boolean {
  return item.is_overdue || item.is_due_today || item.priority === 'urgent' || item.priority === 'high';
}

function isBlockedOrWaiting(item: QueueItem): boolean {
  return item.status === 'blocked' || item.status === 'waiting' || item.blocked_reason !== null || item.waiting_on !== null;
}

function typeIs(item: QueueItem, types: readonly string[]): boolean {
  return item.type !== null && types.includes(item.type);
}

const ROLE_PROFILES: Record<string, RoleProfile> = {
  operations: {
    eyebrow: 'Operations handoff',
    title: 'Vince Handoff Desk',
    mission: 'Schedule, vendor, parts, warranty, and customer-update follow-ups that need operational ownership.',
    primaryMetric: 'handoffs',
    pressureLabel: 'needs operations now',
    lanes: [
      {
        id: 'today',
        title: 'Needs movement now',
        empty: 'No overdue, due-today, urgent, or high-priority handoffs.',
        matches: needsToday,
      },
      {
        id: 'blocked',
        title: 'Blocked or waiting',
        empty: 'No blocked or waiting operational items.',
        matches: isBlockedOrWaiting,
      },
      {
        id: 'parts-vendor',
        title: 'Parts and vendor holds',
        empty: 'No active parts or vendor follow-ups.',
        matches: (item) => typeIs(item, ['vendor_followup', 'parts_eta']),
      },
      {
        id: 'handoff',
        title: 'Schedule and customer handoffs',
        empty: 'No schedule, customer-update, warranty, or ops handoff items.',
        matches: (item) =>
          typeIs(item, ['ops_handoff', 'schedule_confirm', 'customer_ops_update', 'warranty_logistics']),
      },
      {
        id: 'production-gap',
        title: 'Production handoff gaps',
        empty: 'No production handoff gaps waiting on ops.',
        matches: (item) => typeIs(item, ['production_handoff_gap']),
      },
      {
        id: 'other',
        title: 'Other active handoffs',
        empty: 'No other operational handoffs.',
        matches: () => true,
      },
    ],
  },
  production: {
    eyebrow: 'Production questions',
    title: 'Nate Production Desk',
    mission: 'Shop feasibility, electrical, QC, and photo-review follow-ups that need production judgment.',
    primaryMetric: 'questions',
    pressureLabel: 'needs production now',
    lanes: [
      {
        id: 'today',
        title: 'Needs answer now',
        empty: 'No overdue, due-today, urgent, or high-priority production questions.',
        matches: needsToday,
      },
      {
        id: 'blocked',
        title: 'Blocked or waiting',
        empty: 'No blocked or waiting production items.',
        matches: isBlockedOrWaiting,
      },
      {
        id: 'feasibility',
        title: 'Feasibility and clarification',
        empty: 'No feasibility or production-clarification questions.',
        matches: (item) => typeIs(item, ['shop_feasibility', 'production_clarification']),
      },
      {
        id: 'electrical',
        title: 'Electrical decisions',
        empty: 'No electrical questions in this queue.',
        matches: (item) => typeIs(item, ['electrical_question']),
      },
      {
        id: 'qc-photo',
        title: 'QC and photo review',
        empty: 'No QC findings or photo reviews.',
        matches: (item) => typeIs(item, ['qc_finding', 'technical_photo_review']),
      },
      {
        id: 'other',
        title: 'Other production questions',
        empty: 'No other production questions.',
        matches: () => true,
      },
    ],
  },
};

const DEFAULT_PROFILE: RoleProfile = {
  eyebrow: 'Owner queue',
  title: 'Role Queue',
  mission: 'Captured Follow-ups that need owner review.',
  primaryMetric: 'items',
  pressureLabel: 'needs attention now',
  lanes: [
    {
      id: 'today',
      title: 'Needs attention now',
      empty: 'No overdue, due-today, urgent, or high-priority items.',
      matches: needsToday,
    },
    {
      id: 'blocked',
      title: 'Blocked or waiting',
      empty: 'No blocked or waiting items.',
      matches: isBlockedOrWaiting,
    },
    {
      id: 'other',
      title: 'Other active items',
      empty: 'No other active items.',
      matches: () => true,
    },
  ],
};

function profileFor(roleKey: string): RoleProfile {
  return ROLE_PROFILES[roleKey] ?? DEFAULT_PROFILE;
}

function laneGroups(items: QueueItem[], lanes: QueueLane[]): Map<string, QueueItem[]> {
  const groups = new Map(lanes.map((lane) => [lane.id, [] as QueueItem[]]));

  for (const item of items) {
    const lane = lanes.find((candidate) => candidate.matches(item));
    if (lane) groups.get(lane.id)?.push(item);
  }

  return groups;
}

function pressureCount(items: QueueItem[]): number {
  return items.filter(needsToday).length;
}

function pressureNote(items: QueueItem[], counts: QueueCounts): string {
  const priorityOnly = items.filter(
    (item) => (item.priority === 'urgent' || item.priority === 'high') && !item.is_overdue && !item.is_due_today,
  ).length;
  return `${counts.overdue} overdue, ${counts.due_today} due today, ${priorityOnly} urgent/high`;
}

export function OwnerQueueView({
  slug,
  displayName,
  roleKey,
}: OwnerQueueViewProps): JSX.Element {
  const [data, setData] = useState<QueueResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const profile = profileFor(roleKey);
  const groups = data ? laneGroups(data.items, profile.lanes) : null;

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/queues/${slug}`, { credentials: 'include' });
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `Request failed: ${res.status}`);
        }
        const payload = (await res.json()) as QueueResponse;
        setData(payload);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <>
      <header className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-cyan-700">
          {profile.eyebrow}
        </div>
        <div className="mt-1 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
              {profile.title}
            </h1>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
              {profile.mission}
            </p>
          </div>
          <div className="rounded-lg bg-white px-3 py-2 text-xs text-slate-500 ring-1 ring-slate-200">
            Source: {displayName}
          </div>
        </div>
      </header>

      {error !== null ? (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-red-200">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="rounded-lg bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200">
          Loading queue...
        </div>
      ) : data !== null ? (
        <>
          <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <PressureCard
              label={profile.primaryMetric}
              value={data.items.length}
              note="active captured follow-ups"
              tone="slate"
            />
            <PressureCard
              label={profile.pressureLabel}
              value={pressureCount(data.items)}
              note={pressureNote(data.items, data.counts)}
              tone={pressureCount(data.items) > 0 ? 'rose' : 'green'}
            />
            <PressureCard
              label="blocked"
              value={data.counts.blocked}
              note="needs a blocker cleared"
              tone={data.counts.blocked > 0 ? 'amber' : 'slate'}
            />
            <PressureCard
              label="oldest active"
              value={formatDate(data.freshness.oldest_active_at)}
              note="last queue touch"
              tone="slate"
            />
          </section>

          <section className="space-y-4">
            {data.items.length === 0 ? (
              <div className="rounded-lg bg-white px-6 py-8 text-center text-sm text-slate-500 shadow-sm ring-1 ring-slate-200">
                No active Captured Follow-ups in this queue.
              </div>
            ) : (
              <div className="grid gap-4 xl:grid-cols-2">
                {profile.lanes.map((lane) => (
                  <QueueLanePanel
                    key={lane.id}
                    lane={lane}
                    items={groups?.get(lane.id) ?? []}
                  />
                ))}
              </div>
            )}
          </section>

          {data.freshness.newest_item_at !== null || data.freshness.oldest_active_at !== null ? (
            <footer className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
              {data.freshness.newest_item_at !== null ? (
                <span>Newest: {formatTimestamp(data.freshness.newest_item_at)}</span>
              ) : null}
              {data.freshness.oldest_active_at !== null ? (
                <span>Oldest active: {formatTimestamp(data.freshness.oldest_active_at)}</span>
              ) : null}
            </footer>
          ) : null}
        </>
      ) : null}
    </>
  );
}

function PressureCard({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string | number;
  note: string;
  tone: 'slate' | 'green' | 'amber' | 'rose';
}): JSX.Element {
  const toneClass: Record<'slate' | 'green' | 'amber' | 'rose', string> = {
    slate: 'text-slate-900',
    green: 'text-emerald-700',
    amber: 'text-amber-700',
    rose: 'text-rose-700',
  };

  return (
    <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${toneClass[tone]}`}>{value}</div>
      <div className="mt-1 text-xs text-slate-500">{note}</div>
    </div>
  );
}

function QueueLanePanel({
  lane,
  items,
}: {
  lane: QueueLane;
  items: QueueItem[];
}): JSX.Element {
  return (
    <section className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-slate-200">
      <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-950">{lane.title}</h2>
        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
          {items.length}
        </span>
      </header>
      {items.length === 0 ? (
        <div className="px-4 py-6 text-sm text-slate-500">{lane.empty}</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((item) => (
            <QueueItemCard key={`${lane.id}-${item.id}`} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}

function QueueItemCard({ item }: { item: QueueItem }): JSX.Element {
  const due = item.due_at ?? item.due_date;

  return (
    <article className="px-4 py-4 hover:bg-slate-50">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {item.is_overdue ? chip('bg-rose-50 ring-rose-300 text-rose-900', 'overdue') : null}
            {item.is_due_today ? chip('bg-amber-50 ring-amber-300 text-amber-900', 'due today') : null}
            {chip(STATUS_CHIP[item.status] ?? STATUS_CHIP.open, item.status)}
            {chip(PRIORITY_CHIP[item.priority] ?? PRIORITY_CHIP.normal, item.priority)}
          </div>
          <p className="mt-2 text-sm font-medium leading-6 text-slate-950">
            {item.description}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span>{normalizeType(item.type)}</span>
            <span>Due: {formatDate(due)}</span>
            <span>Updated: {formatTimestamp(item.updated_at)}</span>
            {item.source ? <span>Source: {item.source}</span> : null}
          </div>
          {item.waiting_on !== null || item.blocked_reason !== null ? (
            <div className="mt-2 space-y-1 text-xs">
              {item.waiting_on !== null ? (
                <p className="text-slate-600">Waiting: {item.waiting_on}</p>
              ) : null}
              {item.blocked_reason !== null ? (
                <p className="text-rose-700">Blocked: {item.blocked_reason}</p>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap gap-2 sm:flex-col sm:text-right">
          {item.contact_id !== null ? (
            <Link
              to={`/contacts/${item.contact_id}`}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 transition hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
            >
              Customer evidence
            </Link>
          ) : null}
          <Link
            to={qaHref(followUpEvidenceQuestion({
              contactId: item.contact_id,
              description: item.description,
              type: item.type,
              source: item.source,
              due,
            }))}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            Ask evidence
          </Link>
        </div>
      </div>
    </article>
  );
}
